import { getPlatform } from "../index"

interface CacheEntry<T = any> {
  data: T
  timestamp: number
  version: number
  expiresAt: number
}

interface CacheOptions {
  ttl?: number // Time to live in milliseconds
  version?: number // Cache version for invalidation
  staleWhileRevalidate?: boolean // Return stale data while fetching fresh
}

const DEFAULT_TTL = 30 * 60 * 1000 // 30 minutes
const DEFAULT_VERSION = 1

/**
 * Remove specific cache entry
 */
async function remove(key: string): Promise<void> {
  try {
    const platform = getPlatform()
    await platform.storage.removeItem(key)
  } catch (error) {
    console.warn(`Cache removal failed for key ${key}:`, error)
  }
}

/**
 * Get data from cache with immediate return priority
 */
async function get<T>(key: string): Promise<T | null> {
  try {
    const platform = getPlatform()
    const cached = await platform.storage.getItem(key)

    if (!cached) return null

    const entry: CacheEntry<T> = JSON.parse(cached)

    const now = Date.now()
    if (now > entry.expiresAt) {
      await remove(key)
      return null
    }

    return entry.data
  } catch (error) {
    console.warn(`Cache read failed for key ${key}:`, error)
    return null
  }
}

/**
 * Set data in cache with TTL and versioning
 */
async function set<T>(
  key: string,
  data: T,
  options: CacheOptions = {}
): Promise<void> {
  try {
    const platform = getPlatform()
    const now = Date.now()
    const ttl = options.ttl ?? DEFAULT_TTL
    const version = options.version ?? DEFAULT_VERSION

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      version,
      expiresAt: now + ttl,
    }

    await platform.storage.setItem(key, JSON.stringify(entry))
  } catch (error) {
    console.warn(`Cache write failed for key ${key}:`, error)
  }
}

/**
 * Check if cached data is stale but still valid
 */
async function isStale(
  key: string,
  maxAge: number = 5 * 60 * 1000
): Promise<boolean> {
  try {
    const platform = getPlatform()
    const cached = await platform.storage.getItem(key)

    if (!cached) return true

    const entry: CacheEntry = JSON.parse(cached)
    const age = Date.now() - entry.timestamp

    return age > maxAge
  } catch (_error) {
    return true
  }
}

/**
 * Clear all cache entries with specific prefix
 */
async function clearPrefix(prefix: string): Promise<void> {
  try {
    const commonKeys = [
      `${prefix}:bottom-nav`,
      `${prefix}:home-sessions`,
      `${prefix}:user-loyalty`,
      `${prefix}:home-data`,
    ]

    const platform = getPlatform()
    await Promise.allSettled(
      commonKeys.map((k) => platform.storage.removeItem(k))
    )
  } catch (error) {
    console.warn(`Cache prefix clear failed for ${prefix}:`, error)
  }
}

/**
 * Get cache statistics
 */
async function getStats(key: string): Promise<{
  exists: boolean
  age: number
  isExpired: boolean
  version: number
} | null> {
  try {
    const platform = getPlatform()
    const cached = await platform.storage.getItem(key)

    if (!cached) {
      return {
        exists: false,
        age: 0,
        isExpired: true,
        version: 0,
      }
    }

    const entry: CacheEntry = JSON.parse(cached)
    const now = Date.now()

    return {
      exists: true,
      age: now - entry.timestamp,
      isExpired: now > entry.expiresAt,
      version: entry.version,
    }
  } catch (_error) {
    return null
  }
}

/**
 * Platform-agnostic aggressive caching utility
 * Uses platform.storage API for localStorage/zaloStorage automatically
 */
export const StorageCache = {
  get,
  set,
  isStale,
  remove,
  clearPrefix,
  getStats,
}

/**
 * Cache key constants for consistency
 */
export const CACHE_KEYS = {
  BOTTOM_NAV: "be:bottom-nav",
  HOME_SESSIONS: "be:home-sessions",
  USER_LOYALTY: "be:user-loyalty",
  HOME_DATA: "be:home-data",
} as const
